import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Building2, Save, ShieldAlert } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { Bezorglocaties } from "@/components/Bezorglocaties";
import { toast } from "sonner";

const VELDEN = [
  { k: "naam", label: "Bedrijfsnaam" },
  { k: "straat", label: "Straat" },
  { k: "huisnummer", label: "Huisnummer" },
  { k: "postcode", label: "Postcode" },
  { k: "plaats", label: "Plaats" },
  { k: "land", label: "Land" },
  { k: "telefoon", label: "Telefoon" },
  { k: "email", label: "E-mail" },
  { k: "fax", label: "Fax" },
  { k: "website", label: "Website" },
  { k: "kvk", label: "KvK-nummer" },
  { k: "btw_nummer", label: "BTW-nummer" },
  { k: "iban", label: "IBAN" },
  { k: "bic", label: "BIC" },
];

export default function InstellingenBedrijf() {
  const { user } = useAuth();
  const isAdmin = user?.rol === "admin";
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [horizon, setHorizon] = useState(5);
  const [savingH, setSavingH] = useState(false);
  const [relVerwijderen, setRelVerwijderen] = useState(false);

  useEffect(() => {
    api.get("/instellingen/bedrijf").then((r) => setForm(r.data || {})).catch(() => {});
    api.get("/instellingen/beveiliging").then((r) => setRelVerwijderen(!!r.data?.relaties_verwijderen)).catch(() => {});
  }, []);

  const toggleRelVerwijderen = async (val) => {
    setRelVerwijderen(val);
    try {
      await api.put("/instellingen/beveiliging", { relaties_verwijderen: val });
      toast.success(val ? "Relaties verwijderen ingeschakeld" : "Relaties verwijderen uitgeschakeld");
    } catch (e) {
      setRelVerwijderen(!val);
      toast.error(e?.response?.status === 403 ? "Alleen admins kunnen dit wijzigen" : "Opslaan mislukt");
    }
  };

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/instellingen/bedrijf", form);
      setForm(data);
      toast.success("Bedrijfsgegevens opgeslagen");
    } catch (e) {
      toast.error(e?.response?.status === 403 ? "Alleen admins kunnen dit wijzigen" : "Opslaan mislukt");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6 max-w-3xl" data-testid="instellingen-bedrijf">
      <div className="flex items-start gap-3">
        <div className="h-11 w-11 rounded-lg bg-secondary flex items-center justify-center"><Building2 className="h-6 w-6 text-primary" /></div>
        <div>
          <h1 className="font-display text-3xl font-medium tracking-tight">Bedrijfsgegevens</h1>
          <p className="text-sm text-muted-foreground mt-1">Verschijnen in de footer van pakbonnen en facturen, en het IBAN in het betaalverzoek.</p>
        </div>
      </div>

      <div className="bg-card border border-border rounded-xl p-6">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {VELDEN.map(({ k, label }) => (
            <div key={k} className={k === "naam" ? "sm:col-span-2" : ""}>
              <Label className="text-xs text-muted-foreground">{label}</Label>
              <Input value={form[k] || ""} disabled={!isAdmin}
                onChange={(e) => set(k, e.target.value)} data-testid={`bedrijf-${k}`} />
            </div>
          ))}
        </div>
        {isAdmin && (
          <div className="mt-6 flex justify-end">
            <Button onClick={save} disabled={saving} data-testid="bedrijf-save">
              <Save className="h-4 w-4 mr-2" />{saving ? "Opslaan…" : "Opslaan"}
            </Button>
          </div>
        )}
        {!isAdmin && <p className="text-xs text-muted-foreground mt-4">Alleen beheerders kunnen deze gegevens wijzigen.</p>}
      </div>

      <Bezorglocaties isAdmin={isAdmin} />

      <div className="bg-card border border-border rounded-xl p-6" data-testid="beveiliging-instelling">
        <h2 className="font-display text-xl font-medium flex items-center gap-2"><ShieldAlert className="h-5 w-5 text-destructive" /> Beveiliging</h2>
        <p className="text-sm text-muted-foreground mt-1 mb-4">
          Ter beveiliging kunnen relaties (organisaties) standaard niet verwijderd worden. Zet deze schakelaar tijdelijk aan
          om een relatie te kunnen verwijderen, en zet hem daarna weer uit.
        </p>
        <div className="flex items-center justify-between gap-4 rounded-lg border border-border p-4">
          <div>
            <div className="text-sm font-medium">Relaties verwijderen toestaan</div>
            <div className="text-xs text-muted-foreground">Wanneer uit, is de verwijderknop bij organisaties geblokkeerd.</div>
          </div>
          <Switch checked={relVerwijderen} disabled={!isAdmin}
            onCheckedChange={toggleRelVerwijderen} data-testid="relaties-verwijderen-switch" />
        </div>
        {!isAdmin && <p className="text-xs text-muted-foreground mt-3">Alleen beheerders kunnen dit wijzigen.</p>}
      </div>
    </div>
  );
}
